import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';
import { apiPost } from '../lib/api';
import { AuthLayout } from '../components/layout/AuthLayout';

const schema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters').max(128),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  if (!token) {
    return (
      <AuthLayout title="Invalid link" subtitle="This reset link is not valid">
        <div className="text-center space-y-5">
          <p className="text-sm text-zinc-500">
            The password reset link is missing or malformed. Please request a new one.
          </p>
          <Link to="/forgot-password" className="inline-block text-sm font-semibold text-[--color-primary] hover:opacity-80">
            Request a new reset link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout title="Password updated" subtitle="Your new password is active">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle size={28} className="text-emerald-500" />
            </div>
          </div>
          <p className="text-sm text-zinc-500">
            Your password has been updated successfully. You can now sign in with your new password.
          </p>
          <button type="button" className="btn-primary w-full" onClick={() => navigate('/login', { replace: true })}>
            Sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  const onSubmit = async (data: FormData) => {
    setApiError('');
    try {
      await apiPost('/customer/auth/reset-password', { token, newPassword: data.newPassword }, { skipAuth: true });
      setSuccess(true);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      setApiError(
        status === 400
          ? 'This reset link is invalid or has already been used. Please request a new one.'
          : 'Something went wrong. Please try again.',
      );
    }
  };

  return (
    <AuthLayout title="Set new password" subtitle="Enter your new password below">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-zinc-700 mb-1.5">New password</label>
          <div className="relative">
            <input id="newPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" className="input-field pr-10" placeholder="Minimum 8 characters" {...register('newPassword')} />
            <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.newPassword && <p className="mt-1 text-xs text-red-500">{errors.newPassword.message}</p>}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-zinc-700 mb-1.5">Confirm password</label>
          <div className="relative">
            <input id="confirmPassword" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" className="input-field pr-10" placeholder="Repeat your password" {...register('confirmPassword')} />
            <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
        </div>

        {apiError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm text-red-600">{apiError}</p>
          </div>
        )}

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
          {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Updating…</> : 'Set new password'}
        </button>

        <p className="text-center text-sm text-zinc-500">
          <Link to="/login" className="font-semibold text-[--color-primary] hover:opacity-80">Back to sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
